import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Coins, Sparkles } from "lucide-react";
import { Badge } from "../../components/ui/badge/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { useOrganizationMessageBalance } from "./use-organization-message-balance.js";
import { BlockedJobsDialog } from "./BlockedJobsDialog.js";

export interface OrganizationMessageBalanceBadgeProps {
  organizationId: number | null | undefined;
  role: string | undefined;
  variant?: "badge" | "card";
  className?: string;
}

function formatCreditsText(val: string | number | bigint, locale: string): string {
  try {
    const bi = typeof val === "bigint" ? val : BigInt(val);
    return new Intl.NumberFormat(locale).format(bi);
  } catch {
    return String(val);
  }
}

export function OrganizationMessageBalanceBadge({
  organizationId,
  role,
  variant = "badge",
  className,
}: OrganizationMessageBalanceBadgeProps): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    isAgent,
    balance,
    blockedJobs,
    loading,
    error,
    refresh,
    resumeBlockedJobs,
    discardBlockedJobs,
  } = useOrganizationMessageBalance(organizationId, role);

  // Requirement: AGENT users must never see numeric credit balances.
  if (isAgent || !organizationId) {
    return null;
  }

  if (loading && !balance) {
    return null;
  }

  if (error || !balance) {
    return null;
  }

  const hasBlockedJobs = blockedJobs != null && blockedJobs.count > 0;
  const formattedAvailable = formatCreditsText(balance.availableCredits, i18n.language);
  const pluralRuleCount = balance.availableCredits === "1" ? 1 : 2;

  if (variant === "card") {
    return (
      <div
        className={`rounded-lg border border-line bg-surface p-3 text-xs grid gap-2 ${className ?? ""}`}
        data-testid="customer-message-balance-card"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-foreground flex items-center gap-1.5">
            <Coins size={14} className="text-muted" aria-hidden="true" />
            {t("messageBalance.title")}
          </span>
          <Badge variant={balance.isUnlimited ? "success" : "default"}>
            {balance.isUnlimited ? t("messageBalance.unlimited") : t("messageBalance.limited")}
          </Badge>
        </div>

        <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-line/60">
          <span className="text-muted">{t("messageBalance.availableCredits")}</span>
          <span className="font-medium text-foreground text-sm tabular-nums" data-testid="customer-balance-available">
            {balance.isUnlimited ? (
              <span className="text-success">{t("messageBalance.unlimited")}</span>
            ) : (
              <LtrValue>{formattedAvailable}</LtrValue>
            )}
          </span>
        </div>

        {hasBlockedJobs ? (
          <button
            type="button"
            className="mt-1 flex items-center justify-between rounded-md border border-warning/40 bg-warning-muted/40 px-2.5 py-1.5 text-xs text-warning hover:bg-warning-muted transition-colors text-start"
            onClick={() => setDialogOpen(true)}
            data-testid="customer-blocked-jobs-trigger"
          >
            <span className="flex items-center gap-1.5 font-medium">
              <AlertTriangle size={13} aria-hidden="true" />
              {t("messageBalance.blockedJobs.cardAlert", { count: blockedJobs.count })}
            </span>
            <span className="underline font-semibold">{t("messageBalance.blockedJobs.reviewAction")}</span>
          </button>
        ) : null}

        {hasBlockedJobs ? (
          <BlockedJobsDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            organizationId={organizationId}
            blockedCount={blockedJobs.count}
            fingerprint={blockedJobs.fingerprint}
            availableCredits={balance.availableCredits}
            isUnlimited={balance.isUnlimited}
            onResume={resumeBlockedJobs}
            onDiscard={discardBlockedJobs}
            onRefresh={refresh}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className ?? ""}`} data-testid="customer-message-balance-badge-container">
      <Badge
        variant={balance.isUnlimited ? "outline" : "default"}
        className="text-[0.75rem] py-0.5 px-2.5 gap-1.5 border-line font-medium"
        data-testid="customer-message-balance-badge"
      >
        {balance.isUnlimited ? (
          <>
            <Sparkles size={12} className="text-success" aria-hidden="true" />
            <span>{t("messageBalance.unlimitedMessaging")}</span>
          </>
        ) : (
          <>
            <Coins size={12} className="text-muted" aria-hidden="true" />
            <span className="tabular-nums">
              {pluralRuleCount === 1
                ? t("messageBalance.creditsBadge_one")
                : t("messageBalance.creditsBadge_other", { count: formattedAvailable })}
            </span>
          </>
        )}
      </Badge>

      {hasBlockedJobs ? (
        <>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-warning-muted px-2.5 py-0.5 text-[0.75rem] font-medium text-warning hover:bg-warning/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
            onClick={() => setDialogOpen(true)}
            data-testid="customer-blocked-jobs-trigger"
          >
            <AlertTriangle size={12} aria-hidden="true" />
            <span>{t("messageBalance.blockedJobs.badgeAlert", { count: blockedJobs.count })}</span>
          </button>

          <BlockedJobsDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            organizationId={organizationId}
            blockedCount={blockedJobs.count}
            fingerprint={blockedJobs.fingerprint}
            availableCredits={balance.availableCredits}
            isUnlimited={balance.isUnlimited}
            onResume={resumeBlockedJobs}
            onDiscard={discardBlockedJobs}
            onRefresh={refresh}
          />
        </>
      ) : null}
    </div>
  );
}
